
import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DialogFooter } from "@/components/ui/dialog";
import { Doctor } from '@/types';
import { Image, Upload } from 'lucide-react';

interface DoctorFormProps {
  doctor: Partial<Doctor>;
  isEditing: boolean;
  onSave: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCancel: () => void;
  imageFile: File | null;
  imagePreview: string;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImageUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const DoctorForm: React.FC<DoctorFormProps> = ({
  doctor,
  isEditing,
  onSave,
  onInputChange,
  onCancel,
  imageFile,
  imagePreview,
  onFileChange,
  onImageUrlChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <label htmlFor="name">Doctor Name</label>
        <Input 
          id="name" 
          name="name"
          value={doctor.name || ''} 
          onChange={onInputChange}
        />
      </div>
      <div className="grid gap-2">
        <label htmlFor="specialty">Specialty</label>
        <Input 
          id="specialty" 
          name="specialty"
          value={doctor.specialty || ''} 
          onChange={onInputChange}
        />
      </div>
      <div className="grid gap-2">
        <label>Profile Image</label>
        <div className="flex flex-col items-center space-y-4">
          <Avatar className="w-32 h-32 border-2 border-gray-200">
            {imagePreview ? (
              <AvatarImage src={imagePreview} alt="Doctor profile preview" />
            ) : (
              <AvatarFallback>
                <Image className="w-12 h-12 text-gray-400" />
              </AvatarFallback>
            )}
          </Avatar>
          
          <div className="flex items-center gap-2 w-full">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1"
              onClick={triggerFileInput}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Image
            </Button>
            <Input
              id="image"
              name="image"
              placeholder="Or paste image URL"
              value={imageFile ? '' : doctor.image || ''}
              onChange={onImageUrlChange}
              className="flex-1"
            />
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*"
              onChange={onFileChange}
              className="hidden"
            />
          </div>
        </div>
      </div>
      <div className="grid gap-2">
        <label htmlFor="bio">Biography</label>
        <Textarea 
          id="bio" 
          name="bio"
          value={doctor.bio || ''} 
          onChange={onInputChange}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <label htmlFor="startTime">Start Time (24h format)</label>
          <Input 
            id="startTime" 
            name="startTime"
            type="time"
            value={doctor.startTime || "09:00"} 
            onChange={onInputChange}
          />
        </div>
        <div className="grid gap-2">
          <label htmlFor="endTime">End Time (24h format)</label>
          <Input 
            id="endTime" 
            name="endTime"
            type="time"
            value={doctor.endTime || "17:00"} 
            onChange={onInputChange}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onSave}>
          {isEditing ? 'Update Doctor' : 'Save Doctor'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default DoctorForm;
